/**
 * Shared Auth Store for Frontend Applications
 * Used by both plutos-core frontend and payment-service frontend
 * 
 * Requires:
 * - Pinia to be installed in the consuming app
 * - Vue 3.x
 */

import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { 
  createAuthService, 
  type AuthService, 
  type AuthUser, 
  type LoginCredentials, 
  type RegisterCredentials,
  type OAuthProvider,
  type LoginResponse,
  type RegisterResponse,
  type OAuthTokensResponse,
  type LegalAcceptanceData,
} from '../auth';

// Singleton auth service instance per API URL
const authServices = new Map<string, AuthService>();
// Store callbacks separately so we can update them
const authCallbacks = new Map<string, Set<(user: AuthUser | null) => void>>();

function getOrCreateAuthService(apiBaseUrl: string, onAuthChange?: (user: AuthUser | null) => void): AuthService {
  // Always register the callback if provided
  if (onAuthChange) {
    if (!authCallbacks.has(apiBaseUrl)) {
      authCallbacks.set(apiBaseUrl, new Set());
    }
    authCallbacks.get(apiBaseUrl)!.add(onAuthChange);
  }

  const existing = authServices.get(apiBaseUrl);
  if (existing) {
    return existing;
  }
  
  // Create service with a dispatcher that calls all registered callbacks
  const service = createAuthService({
    apiBaseUrl,
    onAuthChange: (user) => {
      const callbacks = authCallbacks.get(apiBaseUrl);
      if (callbacks) {
        callbacks.forEach(cb => cb(user));
      }
    },
  });
  
  authServices.set(apiBaseUrl, service);
  return service;
}

export interface UseAuthStoreOptions {
  apiBaseUrl?: string;
  onAuthChange?: (user: AuthUser | null) => void;
}

/**
 * Create a shared auth store factory
 * This allows different apps to configure their own API URL
 */
export function createAuthStore(options: UseAuthStoreOptions = {}) {
  const apiBaseUrl = options.apiBaseUrl || '';
  
  return defineStore('auth', () => {
    // Reactive state
    const user = ref<AuthUser | null>(null);
    const accessToken = ref<string | null>(null);
    const initialized = ref(false);
    const loading = ref(false);
    const error = ref<string | null>(null);

    // Sync state from service - must be defined before creating service
    function syncState() {
      const state = service.getState();
      user.value = state.user;
      accessToken.value = state.accessToken;
      initialized.value = state.initialized;
      loading.value = state.loading;
      error.value = state.error;
    }

    // Wrap onAuthChange to also sync store state
    const wrappedOnAuthChange = (authUser: AuthUser | null) => {
      // Sync store state whenever auth state changes (including background refreshes)
      syncState();
      // Call user's callback if provided
      options.onAuthChange?.(authUser);
    };

    // Get or create auth service with wrapped callback
    const service = getOrCreateAuthService(apiBaseUrl, wrappedOnAuthChange);

    // Computed
    const isAuthenticated = computed(() => !!accessToken.value && !!user.value);
    const isEmailVerified = computed(() => !!user.value?.emailConfirmedAt);
    // Alias for backwards compatibility
    const isVerified = isEmailVerified;
    
    // Clear error
    function clearError() {
      error.value = null;
    }

    // Actions
    async function initialize(): Promise<AuthUser | null> {
      loading.value = true;
      error.value = null;
      
      try {
        const result = await service.initialize();
        syncState();
        return result;
      } catch (err) {
        error.value = err instanceof Error ? err.message : 'Initialization failed';
        syncState();
        return null;
      }
    }

    async function login(credentials: LoginCredentials): Promise<LoginResponse> {
      loading.value = true;
      error.value = null;
      
      try {
        const result = await service.login(credentials);
        syncState();
        return result;
      } catch (err) {
        error.value = err instanceof Error ? err.message : 'Login failed';
        loading.value = false;
        throw err;
      }
    }

    async function register(credentials: RegisterCredentials): Promise<RegisterResponse> {
      loading.value = true;
      error.value = null;
      
      try {
        const result = await service.register(credentials);
        syncState();
        return result;
      } catch (err) {
        error.value = err instanceof Error ? err.message : 'Registration failed';
        loading.value = false;
        throw err;
      }
    }

    async function logout() {
      try {
        await service.logout();
        syncState();
      } catch (err) {
        console.error('[AuthStore] Logout error:', err);
        syncState();
      }
    }

    async function refreshToken(): Promise<boolean> {
      try {
        const result = await service.refreshToken();
        syncState();
        return result;
      } catch (err) {
        console.error('[AuthStore] Token refresh error:', err);
        syncState();
        return false;
      }
    }

    async function loginWithOAuth(provider: OAuthProvider['provider'], allowRegistration: boolean = false) {
      loading.value = true;
      error.value = null;
      
      try {
        const url = await service.getOAuthUrl({ provider, allowRegistration });
        // Redirect to OAuth provider
        window.location.href = url;
      } catch (err) {
        error.value = err instanceof Error ? err.message : 'OAuth failed';
        loading.value = false;
        throw err;
      }
    }

    async function handleOAuthCallback(searchParams: string): Promise<LoginResponse> {
      loading.value = true;
      error.value = null;
      
      try {
        const params = new URLSearchParams(searchParams);
        const result = await service.handleOAuthCallback(params);
        syncState();
        return result;
      } catch (err: any) {
        error.value = err instanceof Error ? err.message : 'OAuth callback failed';
        syncState();
        // Re-throw with error code preserved
        throw err;
      }
    }

    async function handleOAuthTokens(accessToken: string, refreshToken: string, allowRegistration: boolean = false): Promise<OAuthTokensResponse> {
      loading.value = true;
      error.value = null;
      
      try {
        const result = await service.handleOAuthTokens(accessToken, refreshToken, allowRegistration);
        syncState();
        return result;
      } catch (err: any) {
        error.value = err instanceof Error ? err.message : 'OAuth token verification failed';
        syncState();
        // Re-throw with error code preserved
        throw err;
      }
    }

    async function acceptLegal(data: LegalAcceptanceData): Promise<void> {
      loading.value = true;
      error.value = null;
      
      try {
        await service.acceptLegal(data);
        loading.value = false;
      } catch (err: any) {
        error.value = err instanceof Error ? err.message : 'Failed to accept legal terms';
        loading.value = false;
        throw err;
      }
    }

    async function resetPassword(email: string) {
      loading.value = true;
      error.value = null;
      
      try {
        await service.resetPassword(email);
        loading.value = false;
      } catch (err) {
        error.value = err instanceof Error ? err.message : 'Password reset failed';
        loading.value = false;
        throw err;
      }
    }

    async function resendVerification() {
      loading.value = true;
      error.value = null;
      
      try {
        await service.resendVerification();
        loading.value = false;
      } catch (err) {
        error.value = err instanceof Error ? err.message : 'Failed to send verification';
        loading.value = false;
        throw err;
      }
    }

    // Convenience methods for OAuth providers
    async function loginWithGoogle(allowRegistration: boolean = false) {
      return loginWithOAuth('google', allowRegistration);
    }

    async function loginWithGithub(allowRegistration: boolean = false) {
      return loginWithOAuth('github', allowRegistration);
    }

    return {
      // State
      user,
      accessToken,
      initialized,
      loading,
      error,
      
      // Computed
      isAuthenticated,
      isEmailVerified,
      isVerified, // Alias for backwards compatibility
      
      // Actions
      initialize,
      login,
      register,
      logout,
      refreshToken,
      clearError,
      loginWithOAuth,
      loginWithGoogle,
      loginWithGithub,
      handleOAuthCallback,
      handleOAuthTokens,
      acceptLegal,
      resetPassword,
      resendVerification,
    };
  });
}

// Default store with env-based API URL (for backwards compatibility)
// Apps should use createAuthStore() with their specific API URL
export const useAuthStore = createAuthStore();

// Re-export types
export type { AuthUser, LoginCredentials, RegisterCredentials, LoginResponse, RegisterResponse, OAuthProvider, OAuthTokensResponse, LegalAcceptanceData };
